import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { FaInstagram, FaLinkedinIn, FaTiktok } from "react-icons/fa";
import { socialAccountsService } from "../../services/companyAuthService";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

// Must match the key written by the marketplace ProfileHeader so a brand can be
// taken back to the influencer's Instagram after connecting their own account.
const PENDING_INSTAGRAM_OPEN_KEY = "infuber:pendingInfluencerInstagramUrl";

/**
 * 🔹 Reusable Social Card Component
 */
const SocialCard = ({
  title,
  description,
  connected,
  loading,
  connecting,
  onConnect,
  onRefresh,
  account,
  Icon,
  comingSoon = false,
}) => {
  return (
    <div className="rounded-2xl border bg-white p-5 shadow-sm hover:shadow-md transition-all">
      
      {/* 🔹 Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {Icon && (
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-tr from-pink-500 via-red-500 to-yellow-400 text-white shadow-sm">
              <Icon className="h-4 w-4" />
            </div>
          )}
          <div>
            <h2 className="font-semibold text-gray-900 text-[10px] md:text-sm">
              {title}
            </h2>
            {comingSoon && (
              <p className="text-[10px] sm:text-xs text-gray-500">
                Coming soon
              </p>
            )}
          </div>
        </div>

        <span
          className={`rounded-full px-3 py-1 font-semibold text-[10px] md:text-sm ${
            connected
              ? "bg-green-100 text-green-700"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          {loading
            ? "Checking..."
            : connected
            ? "Connected"
            : comingSoon
            ? "Planned"
            : "Not connected"}
        </span>
      </div>

      {/* 🔹 Description */}
      {description ? (
        <p className="mt-2 text-gray-600 text-[10px] md:text-sm leading-relaxed">
          {description}
        </p>
      ) : null}

      {/* 🔹 Username Row */}
      {connected && account && (
        <div className="mt-4 flex items-center justify-between">
          <div className="text-gray-500 text-[10px] md:text-sm">
            Username: <span className="font-semibold text-gray-900 text-xs sm:text-sm lg:text-lg">
            @{account?.profile?.username}
          </span>
          </div>
          
        </div>
      )}

      {/* 🔹 Stats Row */}
      {connected && account && (
        <div className="mt-4 grid grid-cols-3 text-center">
          <div>
            <p className="text-gray-500 text-[10px] md:text-sm">
              Followers
            </p>
            <p className="font-bold text-gray-900 text-[10px] md:text-sm">
              {account?.profile?.followers_count ?? "-"}
            </p>
          </div>

          <div>
            <p className="text-gray-500 text-[10px] md:text-sm">
              Following
            </p>
            <p className="font-bold text-gray-900 text-[10px] md:text-sm">
              {account?.profile?.follows_count ?? "-"}
            </p>
          </div>

          <div>
            <p className="text-gray-500 text-[10px] md:text-sm">
              Media
            </p>
            <p className="font-bold text-gray-900 text-[10px] md:text-sm">
              {account?.profile?.media_count ?? "-"}
            </p>
          </div>
        </div>
      )}

      {/* 🔹 Buttons (BOTTOM aligned) */}
      {!comingSoon && (
        <div className="mt-4 flex gap-2">
          <button
            onClick={onConnect}
            disabled={connecting}
            className="flex-1 rounded-lg bg-orange-600 px-2 py-2 font-semibold text-white hover:bg-orange-700 disabled:opacity-60 transition text-[10px] md:text-sm"
          >
            {connecting
              ? "Redirecting..."
              : connected
              ? "Reconnect"
              : "Connect"}
          </button>

          <button
            onClick={onRefresh}
            disabled={loading}
            className="flex-1 rounded-lg border border-gray-200 px-2 py-2 font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-60 transition text-[10px] md:text-sm"
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      )}
    </div>
  );
};

const CompanySocialAccountsPage = () => {
  const query = useQuery();

  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);

  const uiMessage = useMemo(() => {
    const connected = query.get("instagramConnected");
    const err = query.get("error");

    if (connected === "true")
      return { type: "success", text: "Instagram connected successfully." };

    if (connected === "false")
      return { type: "error", text: err || "Instagram connection failed." };

    return null;
  }, [query]);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await socialAccountsService.getInstagramStatus();
      setStatus(data);
    } catch (e) {
      setError(e.message || "Failed to load status.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  // Persist a pending "open influencer Instagram" target so it survives the
  // Instagram OAuth round-trip and can be opened once the brand is connected.
  useEffect(() => {
    const openInstagram = query.get("openInstagram");
    if (!openInstagram) return;
    try {
      sessionStorage.setItem(PENDING_INSTAGRAM_OPEN_KEY, openInstagram);
    } catch (_) {
      // Ignore storage errors (e.g. private mode).
    }
  }, [query]);

  const handleConnectInstagram = async () => {
    try {
      setConnecting(true);
      setError(null);

      const redirectTo = `${window.location.origin}/company/dashboard/social-accounts`;

      const data =
        await socialAccountsService.getInstagramConnectUrl(redirectTo);

      if (data?.authUrl) window.location.href = data.authUrl;
    } catch (e) {
      setError(e.message || "Failed to connect.");
    } finally {
      setConnecting(false);
    }
  };

  const connected = Boolean(status?.connected);
  const account = status?.account;

  return (
    <div className="bg-[#F4F2EF] h-full w-full">
      <div className="w-full bg-white/70 backdrop-blur-sm rounded-2xl shadow-sm border border-orange-50 p-4">
        {/* Header - aligned with pipeline styling */}
        <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-sm md:text-xl font-bold text-gray-900 tracking-tight">
              Social accounts
            </h1>
            <p className="mt-1 text-gray-600 text-[10px] md:text-xs max-w-2xl">
              Connect your brand&apos;s social profiles to Infuber so you can
              run automated outreach, track performance, and keep campaigns in
              sync with your pipeline.
            </p>
          </div>
        </header>

        {/* Top divider touching navbar/sidebar */}
        <div className="mt-4 -mx-4 sm:-mx-4 lg:-mx-4 h-px bg-[#E3DFD9]" />

        {/* Alerts */}
        {uiMessage && (
          <div
            className={`mt-6 rounded-lg px-4 py-3 ${
              uiMessage.type === "success"
                ? "bg-green-50 text-green-700"
                : "bg-red-50 text-red-700"
            }`}
          >
            <p className="text-xs sm:text-sm lg:text-base font-medium">
              {uiMessage.text}
            </p>
          </div>
        )}

        {error && (
          <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-red-700">
            <p className="text-xs sm:text-sm lg:text-base">{error}</p>
          </div>
        )}

        {/* Social Cards Grid */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {/* Instagram */}
          <SocialCard
            title="Instagram"
            description={null}
            connected={connected}
            loading={loading}
            connecting={connecting}
            onConnect={handleConnectInstagram}
            onRefresh={fetchStatus}
            account={account}
            Icon={FaInstagram}
          />

          {/* Future Ready Cards */}
          <SocialCard
            title="LinkedIn"
            description={null}
            connected={false}
            loading={false}
            connecting={false}
            Icon={FaLinkedinIn}
            comingSoon
          />

          <SocialCard
            title="TikTok"
            description={null}
            connected={false}
            loading={false}
            connecting={false}
            Icon={FaTiktok}
            comingSoon
          />
        </div>
      </div>
    </div>
  );
};

export default CompanySocialAccountsPage;


// import React, { useEffect, useMemo, useState } from "react";
// import { useLocation } from "react-router-dom";
// import { socialAccountsService } from "../../services/companyAuthService";

// function useQuery() {
//   const { search } = useLocation();
//   return useMemo(() => new URLSearchParams(search), [search]);
// }

// const CompanySocialAccountsPage = () => {
//   const query = useQuery();
//   const [loading, setLoading] = useState(true);
//   const [connecting, setConnecting] = useState(false);
//   const [error, setError] = useState(null);
//   const [status, setStatus] = useState(null);

//   const uiMessage = useMemo(() => {
//     const connected = query.get("instagramConnected");
//     const err = query.get("error");
//     if (connected === "true") return { type: "success", text: "Instagram connected successfully." };
//     if (connected === "false") return { type: "error", text: err || "Instagram connection failed." };
//     return null;
//   }, [query]);

//   const fetchStatus = async () => {
//     try {
//       setLoading(true);
//       setError(null);
//       const data = await socialAccountsService.getInstagramStatus();
//       setStatus(data);
//     } catch (e) {
//       setError(e.message || "Failed to load social account status.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchStatus();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   const handleConnectInstagram = async () => {
//     try {
//       setConnecting(true);
//       setError(null);
//       const redirectTo = `${window.location.origin}/company/dashboard/social-accounts`;
//       const data = await socialAccountsService.getInstagramConnectUrl(redirectTo);
//       if (data?.authUrl) window.location.href = data.authUrl;
//       else setError("Unable to start Instagram connect. Please try again.");
//     } catch (e) {
//       setError(e.message || "Failed to start Instagram connect.");
//     } finally {
//       setConnecting(false);
//     }
//   };

//   const connected = Boolean(status?.connected);
//   const account = status?.account;

//   return (
//     <div className="w-full px-4 sm:px-6 lg:px-10 py-6">
//       <div className="max-w-5xl">
//         <div className="mb-5">
//           <h1 className="font-bold text-gray-900 text-lg lg:text-2xl">
//             Social Accounts
//           </h1>
//           <p className="mt-1 text-gray-600 text-[10px] md:text-sm">
//             Connect your Instagram account to enable upcoming Instagram DM features.
//           </p>
//         </div>

//         {uiMessage && (
//           <div
//             className={`mb-4 rounded-lg border px-4 py-3 ${
//               uiMessage.type === "success"
//                 ? "border-green-200 bg-green-50 text-green-800"
//                 : "border-red-200 bg-red-50 text-red-800"
//             }`}
//           >
//             <p className="text-[10px] md:text-sm font-medium">
//               {uiMessage.text}
//             </p>
//           </div>
//         )}

//         {error && (
//           <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
//             <p className="text-[10px] md:text-sm text-red-800">{error}</p>
//           </div>
//         )}

//         <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
//           <div className="rounded-xl border bg-white p-5 shadow-sm">
//             <div className="flex items-start justify-between gap-3">
//               <div>
//                 <h2 className="font-semibold text-gray-900 text-sm sm:text-base lg:text-lg">
//                   Instagram
//                 </h2>
//                 <p className="mt-1 text-gray-600 text-[10px] md:text-sm">
//                   Connect once. We’ll securely store your access token (encrypted) and use it for DM automation later.
//                 </p>
//               </div>

//               <span
//                 className={`shrink-0 rounded-full px-3 py-1 text-[10px] md:text-sm font-semibold ${
//                   connected
//                     ? "bg-green-100 text-green-800"
//                     : "bg-gray-100 text-gray-700"
//                 }`}
//               >
//                 {loading ? "Checking..." : connected ? "Connected" : "Not connected"}
//               </span>
//             </div>

//             <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:gap-3">
//               <button
//                 type="button"
//                 onClick={handleConnectInstagram}
//                 disabled={connecting}
//                 className="w-full sm:w-auto rounded-lg bg-orange-600 px-4 py-2.5 font-semibold text-white hover:bg-orange-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors text-[10px] md:text-sm"
//               >
//                 {connecting ? "Redirecting..." : connected ? "Reconnect Instagram" : "Connect Instagram"}
//               </button>

//               <button
//                 type="button"
//                 onClick={fetchStatus}
//                 disabled={loading}
//                 className="mt-2 sm:mt-0 w-full sm:w-auto rounded-lg border border-gray-200 bg-white px-4 py-2.5 font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors text-[10px] md:text-sm"
//               >
//                 {loading ? "Refreshing..." : "Refresh Status"}
//               </button>
//             </div>
//           </div>

//           <div className="rounded-xl border bg-white p-5 shadow-sm">
//             <h2 className="font-semibold text-gray-900 text-sm sm:text-base lg:text-lg">
//               Connected Details
//             </h2>
//             <p className="mt-1 text-gray-600 text-[10px] md:text-sm">
//               These are the details saved on the company side after OAuth callback.
//             </p>

//             <div className="mt-4 rounded-lg border border-gray-100 bg-gray-50 p-4">
//               {loading ? (
//                 <p className="text-gray-700 text-[10px] md:text-sm">Loading...</p>
//               ) : !connected ? (
//                 <p className="text-gray-700 text-[10px] md:text-sm">
//                   No Instagram account connected yet.
//                 </p>
//               ) : (
//                 <div className="space-y-2">
//                   <div className="flex items-start justify-between gap-3">
//                     <p className="text-gray-600 text-[10px] md:text-sm">Username</p>
//                     <p className="text-gray-900 font-semibold text-[10px] md:text-sm">
//                       @{account?.username || "-"}
//                     </p>
//                   </div>

//                   <div className="flex items-start justify-between gap-3">
//                     <p className="text-gray-600 text-[10px] md:text-sm">Account Type</p>
//                     <p className="text-gray-900 font-semibold text-[10px] md:text-sm">
//                       {account?.accountType || "-"}
//                     </p>
//                   </div>

//                   <div className="flex items-start justify-between gap-3">
//                     <p className="text-gray-600 text-[10px] md:text-sm">Instagram User ID</p>
//                     <p className="text-gray-900 font-mono text-[10px] md:text-sm">
//                       {account?.instagramUserId || "-"}
//                     </p>
//                   </div>

//                   <div className="pt-2">
//                     <p className="text-gray-600 text-[10px] md:text-sm mb-1">
//                       Meta (saved)
//                     </p>
//                     <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
//                       <div className="rounded-md bg-white border border-gray-200 p-3">
//                         <p className="text-gray-600 text-[10px] md:text-sm">Followers</p>
//                         <p className="font-bold text-gray-900 text-[10px] md:text-sm">
//                           {account?.metadata?.followersCount ?? "-"}
//                         </p>
//                       </div>
//                       <div className="rounded-md bg-white border border-gray-200 p-3">
//                         <p className="text-gray-600 text-[10px] md:text-sm">Following</p>
//                         <p className="font-bold text-gray-900 text-[10px] md:text-sm">
//                           {account?.metadata?.followsCount ?? "-"}
//                         </p>
//                       </div>
//                       <div className="rounded-md bg-white border border-gray-200 p-3">
//                         <p className="text-gray-600 text-[10px] md:text-sm">Media</p>
//                         <p className="font-bold text-gray-900 text-[10px] md:text-sm">
//                           {account?.metadata?.mediaCount ?? "-"}
//                         </p>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default CompanySocialAccountsPage;

